import { HOLD_AREA_TO_LABEL, InventoryHoldArea, OFF_SPEC_AREA_ELIGIBLE_EXTERNAL_SYSTEM_IDS } from "./constants"
import type { InventoryLotReference, InventoryLotWithPallet } from "./types"

import type { InventoryPalletLot, WarehouseArea, WarehouseShelving } from "@/graphql/codegen/graphql"
import { InventoryAdjustmentStatus } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"
import { roundToPrecision } from "@/utils/number"
import { isNil } from "@/utils/utils"

export const formatInventoryLocation = (location: string | null | undefined): string => {
  if (!location) return ""
  return HOLD_AREA_TO_LABEL[location as InventoryHoldArea] ?? location
}

export const getHoldAreaOptions = (warehouseArea?: WarehouseArea | null): Option<InventoryHoldArea>[] => {
  const holdAreas = [InventoryHoldArea.InboundHold, InventoryHoldArea.OutboundHold, InventoryHoldArea.Warehouse]

  if (OFF_SPEC_AREA_ELIGIBLE_EXTERNAL_SYSTEM_IDS.includes(warehouseArea?.externalSystemId ?? "")) {
    holdAreas.push(InventoryHoldArea.OffSpecArea)
  }

  return holdAreas.map((holdArea) => ({ value: holdArea, label: HOLD_AREA_TO_LABEL[holdArea] }))
}

export const isLotLocked = (lot: InventoryPalletLot): boolean => {
  return lot.isLocked || lot.isLockedByOpenGr || lot.adjustmentSyncStatus === InventoryAdjustmentStatus.PendingSync
}

export const filterUntestedLots = (selectedLots: InventoryLotWithPallet[]): InventoryLotReference[] =>
  selectedLots
    .filter((lot) => !lot.hasProductTest)
    .map((lot) => ({
      lotNumber: lot.lotNbr ?? "",
      batchNumber: lot.batchNbr ?? "",
      palletReferenceNumber: lot.pallet.referenceNbr,
    }))

export const findFirstAvailableShelving = (
  shelvings: WarehouseShelving[],
  aisleCode: string,
  sectionCode?: string
): WarehouseShelving | null =>
  shelvings
    .filter(
      (shelving) =>
        shelving.aisleCode === aisleCode &&
        (!sectionCode || shelving.sectionCode === sectionCode) &&
        !isNil(shelving.binNumber)
    )
    .sort((a, b) => {
      const sectionCompare = (a.sectionCode ?? "").localeCompare(b.sectionCode ?? "")

      if (sectionCompare !== 0) return sectionCompare

      return (a.binNumber ?? 0) - (b.binNumber ?? 0)
    })[0] ?? null

export const calculateQuantityDifference = (quantity: number | null, originalQuantity: number): number | null =>
  isNil(quantity) ? null : roundToPrecision(quantity - originalQuantity, 2) + 0
